// src/encounter/services/use-drug-interactions.ts
// Runs the drug-interaction engine inline at medication entry (PROD parity — Flutter
// `EPrescriptionBloc.getDrugInteractions` → `PatientChartRepository.getDrugInteractions`).
//
// Wire (Flutter `patient_chart_data_source_impl.dart:948`):
//
//   POST /api/EPriscription/PrescriptionAlert
//        body: DrugInteractionInputModel { subject, performer, dxCode[], drugInfo[] }
//        → PatientDrugInteraction[]
//
// **Backend typo preserved**: the service base is `/api/EPriscription` (should be "EPrescription").
// The .NET route is defined with the typo, so the correctly-spelled path 404s — same as the GET
// alerts endpoint in `use-patient-drug-interactions.ts`. DO NOT "fix".
//
// This is the SAME interaction engine the persisted-alerts GET surfaces on the chart; here it is run
// speculatively for the med the clinician is about to add (drug-drug / drug-allergy / drug-disease),
// so the warning appears BEFORE the Rx is committed. Flutter returns `[]` on 404 (no alerts is the
// clinically desirable state) — mirrored here so a "no interactions" response renders the positive
// confirmation rather than an error.
import { useQuery } from '@tanstack/react-query';
import { post, ApiError } from '@/shared/api/api-client';
import type { PatientDrugInteraction } from '@/types/patient-chart-api';
import type { DrugInteractionInput } from '../lib/to-drug-interaction-input';
import { useIsDrugInteractionEnabled } from './use-organisation-features';

/**
 * Speculative drug-interaction check for the medication being entered.
 *
 * `enabled` gates the fetch: pass `false` (or omit patient/med context) to keep it dormant — the
 * hook must always be CALLED (Rules of Hooks) but only runs when there is something to check. The
 * query key includes the full input so re-selecting a med / changing the dose or diagnoses re-runs
 * the engine (Flutter re-dispatches `FetchDrugInteractionEvent` on every change).
 *
 * The engine also ONLY runs when the org has turned drug-interaction checking on and is on a
 * Vidal-coded (or unset) medication system — Flutter `isDrugInteractionEnable`. This gate is folded
 * in here (not at the call sites) so every host inherits it automatically; see
 * `useIsDrugInteractionEnabled`.
 */
export function useDrugInteractions(
  input: DrugInteractionInput | null | undefined,
  enabled = true,
) {
  const featureEnabled = useIsDrugInteractionEnabled();
  const hasDrug = !!input && (input.drugInfo?.length ?? 0) > 0;
  return useQuery<PatientDrugInteraction[]>({
    queryKey: ['drug-interactions', input],
    queryFn: async ({ signal }) => {
      try {
        return await post<PatientDrugInteraction[]>(
          '/api/EPriscription/PrescriptionAlert',
          input,
          { signal },
        );
      } catch (e) {
        // Flutter: DioException 404 → []. No triggered alerts is the desirable state.
        // CAUTION (PROD-2721): this makes `[]` ambiguous — "the engine found nothing" and "there was
        // no engine to ask" look the same, and the licensed engine (Vidal) has expired. So an empty
        // result is NEVER an all-clear: do not render a "no interaction" confirmation from it. The
        // R1 limit is said by NoMedicationChecksNotice instead.
        if (e instanceof ApiError && e.status === 404) return [];
        throw e;
      }
    },
    enabled: enabled && hasDrug && featureEnabled,
    // A safety check must never be served from cache across patient-state changes. The backend
    // derives alerts from hidden patient state (current meds / allergies / conditions) that is NOT
    // in the query key, and the clinical save hooks do not invalidate `drug-interactions`. Caching
    // could therefore surface a stale prior warning — or a falsely reassuring no-alert result — when
    // the same drug is reopened after that state changed. Keep it always-stale so every dialog open
    // re-runs the engine against current state (refetchOnMount defaults to refetching a stale query).
    staleTime: 0,
    gcTime: 0,
  });
}
